import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { api } from "@/lib/api";
import OfferCard from "@/components/OfferCard";
import { useSEO } from "@/lib/useSEO";
import { decideRobots, buildCanonical, buildPageMeta } from "@/lib/seoIndex";
import {
  ArrowRight,
  Sparkles,
  Heart,
  MapPin,
  ShieldCheck,
  FileSearch,
  Building2,
  CalendarDays,
} from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1576267423445-b2e0074d68a4?w=1200&q=80&auto=format&fit=crop";
const HERO_ALT = "A diverse team of volunteers collaborating around a laptop";

// 6 strongest hubs — chosen for clarity over geographic completeness.
const FEATURED_CITIES = ["Berlin", "Munich", "Hamburg", "Frankfurt", "Vienna", "Zurich"];

// Real causes with enough depth in the current dataset, in editorial order.
const FEATURED_CAUSES = [
  "Poverty & Hunger",
  "Education",
  "Climate & Environment",
  "Health & Wellbeing",
  "Refugees & Migration",
  "Mental Health",
];

export default function LandingPage() {
  const { t, i18n } = useTranslation();
  const [featured, setFeatured] = useState([]);
  const [stats, setStats] = useState({ offers: 0, countries: 0, organizations: 0 });
  const [cities, setCities] = useState([]);
  const [causes, setCauses] = useState([]);
  const [refreshStatus, setRefreshStatus] = useState({
    is_today: false,
    last_refreshed_at: null,
    is_recent: false,
  });

  useEffect(() => {
    api.get("/offers", { params: { limit: 6, prominent_only: true } }).then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/cities").then((r) => setCities(r.data || [])).catch(() => {});
    api.get("/causes").then((r) => setCauses(r.data || [])).catch(() => {});
    api
      .get("/refresh/status")
      .then((r) => {
        const lastRaw = r.data?.last_refreshed_at || null;
        const lastDate = lastRaw ? new Date(lastRaw) : null;
        const isRecent =
          lastDate &&
          Number.isFinite(lastDate.getTime()) &&
          Date.now() - lastDate.getTime() < 14 * 24 * 60 * 60 * 1000;
        setRefreshStatus({
          is_today: Boolean(r.data?.is_today),
          last_refreshed_at: lastRaw,
          is_recent: Boolean(isRecent),
        });
      })
      .catch(() => {});
  }, []);

  // Freshness label logic:
  //   - is_today true       -> "Today" / "Heute"
  //   - last_refreshed_at present AND within 14 days -> localized date
  //   - else                -> "Regularly" / "Regelmäßig"
  // We never claim "daily" without a real timestamp.
  const refreshIsToday = refreshStatus.is_today;
  const refreshDate = refreshStatus.last_refreshed_at
    ? new Date(refreshStatus.last_refreshed_at)
    : null;
  const refreshDateLocalized =
    refreshStatus.is_recent && refreshDate
      ? refreshDate.toLocaleDateString(i18n.language || "en", { day: "numeric", month: "short" })
      : null;
  const heroCardEyebrow = refreshIsToday
    ? t("landing.hero_card_eyebrow_today", { defaultValue: "Updated today" })
    : t("landing.hero_card_eyebrow_regular", { defaultValue: "Regularly updated" });
  const trustStatUpdatedValue = refreshIsToday
    ? t("landing.trust_stat_updated_today", { defaultValue: "Today" })
    : refreshDateLocalized
    ? refreshDateLocalized
    : t("landing.trust_stat_updated_regular", { defaultValue: "Regular" });

  const homeMeta = buildPageMeta("home");
  useSEO({
    robots: decideRobots({ pageType: "home" }),
    title: homeMeta.title,
    description: homeMeta.description,
    canonical: buildCanonical("/"),
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "What is doing-good.org?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "doing-good.org is an open, free directory of volunteering, donation and pro bono opportunities from NGOs and community organisations across Europe. Every listing links directly to the original organisation.",
          },
        },
        {
          "@type": "Question",
          name: "How are organizations selected?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "We source from public NGO platforms (betterplace.org, vostel.de) and a hand-curated list of well-known European charities. Each project is reviewed for transparency, accuracy and legitimacy before publishing.",
          },
        },
        {
          "@type": "Question",
          name: "How often are opportunities updated?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Our pipeline refreshes regularly from public NGO sources so listings stay current.",
          },
        },
        {
          "@type": "Question",
          name: "Is doing-good.org free?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. We never charge donors, volunteers or organisations. There is no commission on donations.",
          },
        },
      ],
    },
  });

  // Cities / causes lookups by display name.
  const cityBySlug = (name) => cities.find((c) => c.city === name);
  const causeBySlug = (name) => causes.find((c) => c.cause === name);
  const featuredCities = FEATURED_CITIES.map(cityBySlug).filter(Boolean);
  const featuredCauses = FEATURED_CAUSES.map(causeBySlug).filter(Boolean);

  return (
    <div data-testid="landing-page">
      {/* SECTION 1 — HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 pt-10 md:pt-14 pb-10 md:pb-14 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 relative z-10">
            <p className="eyebrow" data-testid="hero-eyebrow">
              {t("landing.hero_eyebrow", { defaultValue: "An independent European civic platform" })}
            </p>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-[#1F2A24]">
              {t("landing.hero_title_1", { defaultValue: "Find ways to help" })}
              <br />
              <span className="text-[#5B8C66]">
                {t("landing.hero_title_2", { defaultValue: "in your community." })}
              </span>
            </h1>
            <p className="mt-6 text-lg text-[#5F6B61] max-w-xl leading-relaxed">
              {t("landing.hero_subtitle", {
                defaultValue:
                  "Discover volunteering, donation and pro bono opportunities from NGOs and community organisations across Europe.",
              })}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to="/offers"
                className="btn-pill inline-flex items-center gap-2 px-6 py-3 bg-[#5B8C66] text-white font-semibold hover:bg-[#3F6147] transition"
                data-testid="hero-cta-explore"
              >
                {t("landing.hero_explore", { defaultValue: "Explore opportunities" })}
                <ArrowRight size={16} strokeWidth={2.2} />
              </Link>
              <Link
                to="/cities"
                className="btn-pill inline-flex items-center gap-2 px-6 py-3 border border-[#DED7C7] hover:border-[#5B8C66] text-[#1F2A24] font-semibold transition"
                data-testid="hero-cta-cities"
              >
                <MapPin size={15} strokeWidth={2} />
                {t("landing.hero_cities", { defaultValue: "Browse cities" })}
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] rounded-[2.5rem] overflow-hidden border border-[#DED7C7] shadow-xl z-10">
              <img src={HERO} alt={HERO_ALT} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1F2A24]/30 via-transparent to-transparent" />
              <div
                className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur rounded-2xl px-4 py-3"
                data-testid="hero-image-trust"
              >
                <p className="eyebrow text-[10px]">
                  {heroCardEyebrow}
                </p>
                <p className="mt-1 text-sm font-semibold text-[#1F2A24]">
                  <Trans
                    i18nKey="landing.hero_card_text"
                    values={{ count: stats.offers || 879 }}
                    defaults="<0>{{count}}</0> active opportunities"
                    components={[<span key="count" className="tabular-nums" />]}
                  />
                </p>
              </div>
            </div>
            <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full bg-[#D89A5C] -z-0 hidden md:block" />
            <div className="absolute -bottom-6 -left-6 w-32 h-32 rounded-full bg-[#E9F0E0] -z-0 hidden md:block" />
          </div>
        </div>
      </section>

      {/* SECTION 2 — TRUST BAR (statistics + sources + learn more) */}
      <section
        aria-label={t("landing.trust_bar_aria", { defaultValue: "Platform transparency" })}
        className="border-y border-[#DED7C7] bg-[#F6F2E8]/70"
        data-testid="trust-bar"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-8 md:py-10 grid lg:grid-cols-12 gap-8 items-center">
          <dl className="lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-5" data-testid="trust-bar-stats">
            <TrustStat
              icon={ShieldCheck}
              value={stats.offers}
              label={t("landing.trust_stat_offers", { defaultValue: "Active opportunities" })}
              testId="trust-stat-offers"
            />
            <TrustStat
              icon={Building2}
              value={stats.organizations}
              label={t("landing.trust_stat_orgs", { defaultValue: "Organizations" })}
              testId="trust-stat-orgs"
            />
            <TrustStat
              icon={MapPin}
              value={cities.length || 0}
              label={t("landing.trust_stat_cities", { defaultValue: "Cities" })}
              testId="trust-stat-cities"
            />
            <TrustStat
              icon={CalendarDays}
              text={trustStatUpdatedValue}
              label={t("landing.trust_stat_updated_label", { defaultValue: "Updated" })}
              testId="trust-stat-updated"
            />
          </dl>

          <div className="lg:col-span-4 lg:border-l lg:border-[#DED7C7] lg:pl-8">
            <p className="text-sm text-[#5F6B61] leading-relaxed">
              {t("landing.trust_bar_caption", {
                defaultValue:
                  "Independent, non-commercial platform. Listings link directly to the source organisation.",
              })}
            </p>
            <Link
              to="/about"
              className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-[#5B8C66] hover:underline"
              data-testid="trust-bar-learn-more"
            >
              {t("landing.trust_bar_learn_more", { defaultValue: "How we work" })}
              <ArrowRight size={12} strokeWidth={2.4} />
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 3 — DISCOVERY HUB (visually dominant) */}
      <section
        className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-20 md:py-28"
        data-testid="discovery-hub"
      >
        <div className="max-w-3xl">
          <p className="eyebrow">{t("landing.discover_eyebrow", { defaultValue: "Discovery" })}</p>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#1F2A24]">
            {t("landing.discover_title", { defaultValue: "Start with a city or cause." })}
          </h2>
          <p className="mt-4 text-lg text-[#5F6B61] leading-relaxed">
            {t("landing.discover_subtitle", {
              defaultValue:
                "Explore opportunities based on where you are or what you care about.",
            })}
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-2 gap-10 lg:gap-14">
          {/* Cities picker */}
          <div data-testid="discovery-cities">
            <div className="flex items-baseline justify-between gap-4">
              <h3 className="text-lg font-bold text-[#1F2A24] inline-flex items-center gap-2">
                <MapPin size={16} strokeWidth={2.2} className="text-[#5B8C66]" />
                {t("landing.discover_cities_title", { defaultValue: "By city" })}
              </h3>
              <Link
                to="/cities"
                className="text-xs font-bold text-[#5B8C66] hover:underline inline-flex items-center gap-1"
                data-testid="discovery-cities-all"
              >
                {t("landing.discover_cities_all", { defaultValue: "All cities" })}
                <ArrowRight size={12} strokeWidth={2.4} />
              </Link>
            </div>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="discovery-city-chips">
              {cities
                .slice()
                .sort((a, b) => b.count - a.count)
                .slice(0, 6)
                .map((c) => (
                  <Link
                    key={c.slug}
                    to={`/c/${c.slug}`}
                    className="flex items-center justify-between rounded-2xl border border-[#DED7C7] bg-white px-3.5 py-3 hover:border-[#5B8C66] hover:shadow-sm transition"
                    data-testid={`discovery-city-${c.slug}`}
                  >
                    <span className="text-sm font-bold text-[#1F2A24]">
                      {t(`cities.${c.city}`, { defaultValue: c.city })}
                    </span>
                    <span className="text-xs font-semibold text-[#5F6B61]">{c.count}</span>
                  </Link>
                ))}
            </div>
          </div>

          {/* Causes picker */}
          <div data-testid="discovery-causes">
            <div className="flex items-baseline justify-between gap-4">
              <h3 className="text-lg font-bold text-[#1F2A24] inline-flex items-center gap-2">
                <Heart size={16} strokeWidth={2.2} className="text-[#5B8C66]" />
                {t("landing.discover_causes_title", { defaultValue: "By cause" })}
              </h3>
              <Link
                to="/causes"
                className="text-xs font-bold text-[#5B8C66] hover:underline inline-flex items-center gap-1"
                data-testid="discovery-causes-all"
              >
                {t("landing.discover_causes_all", { defaultValue: "All causes" })}
                <ArrowRight size={12} strokeWidth={2.4} />
              </Link>
            </div>
            <div className="mt-5 flex flex-wrap gap-2.5" data-testid="discovery-cause-chips">
              {causes
                .slice()
                .sort((a, b) => b.count - a.count)
                .slice(0, 6)
                .map((c) => (
                  <Link
                    key={c.slug}
                    to={`/cause/${c.slug}`}
                    className="inline-flex items-center gap-2 rounded-full border border-[#DED7C7] bg-white px-3.5 py-2 text-sm hover:border-[#5B8C66] hover:text-[#5B8C66] transition"
                    data-testid={`discovery-cause-${c.slug}`}
                  >
                    {t(`causes.${c.cause}`, { defaultValue: c.cause })}
                    <span className="text-xs font-semibold text-[#9AA597]">{c.count}</span>
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4 — FEATURED OPPORTUNITIES */}
      <section
        className="border-t border-[#DED7C7] bg-white"
        data-testid="featured-section"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-20">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <p className="eyebrow">{t("landing.featured_eyebrow", { defaultValue: "Latest" })}</p>
              <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">
                {t("landing.featured_title", { defaultValue: "Recently added opportunities" })}
              </h2>
              <p className="mt-2 text-sm text-[#5F6B61]">
                {t("landing.featured_subtitle", { defaultValue: "Updated daily from listed organisations." })}
              </p>
            </div>
            <Link
              to="/offers"
              className="text-sm font-bold text-[#5B8C66] hover:underline inline-flex items-center gap-1"
              data-testid="featured-view-all"
            >
              {t("landing.view_all", { defaultValue: "View all" })}
              <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="featured-offers-grid">
            {featured.map((o) => (
              <OfferCard key={o.id} offer={o} />
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — FEATURED CITIES (compact, 6 strongest hubs) */}
      {featuredCities.length > 0 && (
        <section
          className="border-t border-[#DED7C7] bg-[#F6F2E8]/60"
          data-testid="featured-cities-section"
        >
          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
              <div>
                <p className="eyebrow">{t("landing.fc_eyebrow", { defaultValue: "Featured cities" })}</p>
                <h2 className="mt-2 text-2xl sm:text-3xl font-black tracking-tight">
                  {t("landing.fc_title", { defaultValue: "Explore by city" })}
                </h2>
              </div>
              <Link
                to="/cities"
                className="text-sm font-bold text-[#5B8C66] hover:underline inline-flex items-center gap-1"
                data-testid="featured-cities-all"
              >
                {t("landing.fc_all", { defaultValue: "All cities" })}
                <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="featured-cities-grid">
              {featuredCities.map((c) => (
                <Link
                  key={c.slug}
                  to={`/c/${c.slug}`}
                  className="card-clay bg-white px-4 py-5 hover:shadow-sm transition"
                  data-testid={`featured-city-${c.slug}`}
                >
                  <p className="text-base font-bold text-[#1F2A24]">
                    {t(`cities.${c.city}`, { defaultValue: c.city })}
                  </p>
                  <p className="mt-1 text-xs text-[#5F6B61]">
                    {t(`countries.${c.country}`, { defaultValue: c.country })}
                  </p>
                  <p className="mt-3 text-xs font-bold text-[#5B8C66]">
                    {c.count}{" "}
                    <span className="text-[#9AA597] font-medium">
                      {t("landing.fc_count_label", { defaultValue: "opportunities" })}
                    </span>
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SECTION 6 — FEATURED CAUSES (compact, 6 real causes) */}
      {featuredCauses.length > 0 && (
        <section
          className="border-t border-[#DED7C7]"
          data-testid="featured-causes-section"
        >
          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
              <div>
                <p className="eyebrow">{t("landing.fcause_eyebrow", { defaultValue: "Featured causes" })}</p>
                <h2 className="mt-2 text-2xl sm:text-3xl font-black tracking-tight">
                  {t("landing.fcause_title", { defaultValue: "Choose a cause you care about" })}
                </h2>
              </div>
              <Link
                to="/causes"
                className="text-sm font-bold text-[#5B8C66] hover:underline inline-flex items-center gap-1"
                data-testid="featured-causes-all"
              >
                {t("landing.fcause_all", { defaultValue: "All causes" })}
                <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="featured-causes-grid">
              {featuredCauses.map((c) => (
                <Link
                  key={c.slug}
                  to={`/cause/${c.slug}`}
                  className="card-clay px-5 py-5 hover:shadow-sm transition flex items-start justify-between gap-3"
                  data-testid={`featured-cause-${c.slug}`}
                >
                  <div>
                    <p className="text-base font-bold text-[#1F2A24]">
                      {t(`causes.${c.cause}`, { defaultValue: c.cause })}
                    </p>
                    <p className="mt-1 text-xs text-[#5F6B61]">
                      {c.count}{" "}
                      {t("landing.fcause_count_label", { defaultValue: "opportunities" })}
                    </p>
                  </div>
                  <ArrowRight size={16} strokeWidth={2} className="text-[#5B8C66] mt-1" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SECTION 7 — WHY WE BUILT DOING-GOOD.ORG */}
      <section
        className="border-t border-[#DED7C7] bg-white"
        data-testid="why-section"
      >
        <div className="max-w-3xl mx-auto px-4 md:px-8 lg:px-12 py-20 md:py-24">
          <p className="eyebrow">{t("landing.why_eyebrow", { defaultValue: "Why this exists" })}</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight text-[#1F2A24]">
            {t("landing.why_title", { defaultValue: "Why we built doing-good.org" })}
          </h2>
          <div className="mt-7 space-y-5 text-[17px] text-[#3A463C] leading-[1.75]">
            <p>
              {t("landing.why_p1", {
                defaultValue:
                  "Finding a trustworthy way to help in your community is harder than it should be. Information about local NGOs is scattered across dozens of disconnected platforms, regional websites and outdated lists.",
              })}
            </p>
            <p>
              {t("landing.why_p2", {
                defaultValue:
                  "doing-good.org brings these opportunities into one calm, searchable place. We review each project for transparency, link directly to the source organisation and keep the directory up to date — without ads, fees or commissions.",
              })}
            </p>
            <p>
              {t("landing.why_p3", {
                defaultValue:
                  "We don't run programs. We don't accept donations on behalf of NGOs. Our only job is to make meaningful opportunities easier to discover.",
              })}
            </p>
          </div>
          <Link
            to="/about"
            className="mt-8 inline-flex items-center gap-1 text-sm font-bold text-[#5B8C66] hover:underline"
            data-testid="why-learn-more"
          >
            {t("landing.why_learn_more", { defaultValue: "Read more about the project" })}
            <ArrowRight size={14} strokeWidth={2.2} />
          </Link>
        </div>
      </section>

      {/* SECTION 8 — FOR ORGANIZATIONS (quiet CTA) */}
      <section className="border-t border-[#DED7C7]" data-testid="for-orgs-section">
        <div className="max-w-5xl mx-auto px-4 md:px-8 lg:px-12 py-20">
          <div className="rounded-[2rem] border border-[#DED7C7] bg-white p-8 md:p-12 grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <p className="eyebrow">{t("landing.org_eyebrow", { defaultValue: "For organisations" })}</p>
              <h2 className="mt-3 text-2xl md:text-3xl font-black tracking-tight text-[#1F2A24]">
                {t("landing.org_title", { defaultValue: "Run an NGO or community project?" })}
              </h2>
              <p className="mt-3 text-[#5F6B61] leading-relaxed">
                {t("landing.org_body", {
                  defaultValue:
                    "List your opportunity, suggest an update or get in touch. Free for non-profits and social initiatives across Europe.",
                })}
              </p>
            </div>
            <div className="md:col-span-4 flex flex-col gap-3">
              <Link
                to="/list-offer"
                className="btn-pill inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#5B8C66] text-white font-semibold hover:bg-[#3F6147] transition"
                data-testid="org-cta-list"
              >
                {t("landing.org_cta_list", { defaultValue: "List an opportunity" })}
                <ArrowRight size={14} strokeWidth={2.2} />
              </Link>
              <a
                href="mailto:info@doing-good.org"
                className="btn-pill inline-flex items-center justify-center gap-2 px-5 py-3 border border-[#DED7C7] hover:border-[#5B8C66] text-[#1F2A24] font-semibold transition"
                data-testid="org-cta-contact"
              >
                {t("landing.org_cta_contact", { defaultValue: "Contact us" })}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 9 — FAQ (structured-data backed by useSEO above) */}
      <section
        className="border-t border-[#DED7C7] bg-white"
        data-testid="faq-section"
      >
        <div className="max-w-3xl mx-auto px-4 md:px-8 lg:px-12 py-20">
          <p className="eyebrow">{t("landing.faq_eyebrow", { defaultValue: "Questions" })}</p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">
            {t("landing.faq_title", { defaultValue: "Frequently asked" })}
          </h2>
          <div className="mt-8 space-y-3">
            {[
              {
                q: t("landing.faq_q1", { defaultValue: "How are organisations selected?" }),
                a: t("landing.faq_a1", {
                  defaultValue:
                    "We aggregate from public NGO platforms (betterplace.org, vostel.de) and a hand-curated list of well-known European charities. Each project is reviewed before publishing.",
                }),
              },
              {
                q: t("landing.faq_q2", { defaultValue: "How do you check organisations?" }),
                a: t("landing.faq_a2", {
                  defaultValue:
                    "We check that each listed organisation is a real, publicly verifiable NGO or community initiative. We do not endorse individual projects beyond confirming the source.",
                }),
              },
              {
                q: t("landing.faq_q3", { defaultValue: "How often are opportunities updated?" }),
                a: t("landing.faq_a3", {
                  defaultValue:
                    "Our pipeline refreshes regularly from public NGO sources so listings stay current.",
                }),
              },
              {
                q: t("landing.faq_q4", { defaultValue: "Is doing-good.org free?" }),
                a: t("landing.faq_a4", {
                  defaultValue:
                    "Yes. We never charge donors, volunteers or organisations. There is no commission on donations.",
                }),
              },
            ].map((f, i) => (
              <details
                key={i}
                className="card-clay px-5 py-4 group bg-white"
                data-testid={`faq-item-${i}`}
              >
                <summary className="font-bold cursor-pointer flex justify-between items-center gap-4">
                  <span>{f.q}</span>
                  <ArrowRight size={14} className="transition group-open:rotate-90 shrink-0" strokeWidth={2.4} />
                </summary>
                <p className="mt-3 text-sm text-[#3A463C] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

/**
 * Single statistic for the trust bar.
 * Accepts either a numeric `value` or a short `text` (e.g. "Daily").
 */
function TrustStat({ icon: Icon, value, text, label, testId }) {
  return (
    <div className="flex items-start gap-3" data-testid={testId}>
      <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-white border border-[#DED7C7] text-[#5B8C66] shrink-0">
        <Icon size={15} strokeWidth={2} />
      </span>
      <div className="min-w-0">
        <dt className="text-[11px] uppercase tracking-wide text-[#5F6B61] font-semibold leading-snug min-h-[2.4em] flex items-start">
          {label}
        </dt>
        <dd className="text-lg font-black text-[#1F2A24] leading-none mt-1 tabular-nums">
          {text ?? (value || 0)}
        </dd>
      </div>
    </div>
  );
}
